// Pure visibility computation for the user avatar popover (shallow items) and
// the Organization settings page (its top tabs), extracted so per-item/per-tab
// RBAC gating can be unit-tested without rendering either. Inputs are
// already-resolved booleans (from useSettingsAccess) — this module holds no
// permission logic of its own.

import type { EnterpriseRequestSource } from 'v2/shared/utils/buddy-enterprise-request'

export interface SettingsMenuItem {
  title: string
  path: string
  visible: boolean
}

// Single source of truth for the shallow popover item paths — also imported
// by user-avatar-popover.tsx to key its icon lookup, so a rename in one place
// can't silently desync the icon lookup from the actual item path.
export const SETTINGS_PATHS = {
  profileSettings: '/profile/details',
  organizationSettings: '/organization-settings',
  billing: '/billing/overview',
  plansAndPricing: '/billing/plans-and-pricing',
  endpoints: '/endpoints',
  geoPermissions: '/geo-permissions',
  trustAndLegal: '/trust-and-legal',
} as const

export interface GetVisibleSettingsItemsInput {
  isAdmin: boolean
  canAccessUsers: boolean
  canAccessBilling: boolean
  canAccessEndpoints: boolean
  ssoSelfServeEnabled: boolean
  canViewTrustAndLegal: boolean
}

export interface GetVisibleSettingsItemsResult {
  profileSettings: SettingsMenuItem
  organizationSettings: SettingsMenuItem
  billing: SettingsMenuItem
  plansAndPricing: SettingsMenuItem
  endpoints: SettingsMenuItem
  geoPermissions: SettingsMenuItem
  trustAndLegal: SettingsMenuItem
}

export type OrganizationSettingsTabKey =
  | 'general'
  | 'apiKeys'
  | 'sso'
  | 'ipWhitelisting'
  | 'users'
  | 'subAccounts'
  | 'voice'
  | 'messaging'
  | 'limits'
  | 'businessVerification'

// Single source of truth for the Organization settings page's tab paths —
// imported by both the popover-sections helper below and the Organization
// settings page itself, so the tab routing can't drift from this list.
export const ORGANIZATION_SETTINGS_TABS: Record<OrganizationSettingsTabKey, string> = {
  general: '/organization-settings/general',
  apiKeys: '/organization-settings/api-keys',
  sso: '/organization-settings/sso',
  ipWhitelisting: '/organization-settings/ip-whitelisting',
  users: '/organization-settings/users',
  subAccounts: '/organization-settings/sub-accounts',
  voice: '/organization-settings/voice',
  messaging: '/organization-settings/messaging',
  limits: '/organization-settings/limits',
  businessVerification: '/organization-settings/business-verification',
}

export interface OrganizationSettingsTab {
  key: OrganizationSettingsTabKey
  title: string
  path: string
  visible: boolean
  // A deep link to this tab waits for the flags to resolve before its access is judged.
  needsFlag?: boolean
}

export interface GetOrganizationSettingsTabsInput {
  isAdmin: boolean
  canAccessUsers: boolean
  // Gates the SAML SSO tab together with isAdmin.
  ssoSelfServeEnabled: boolean
  // India annual business verification needs action or is in review (useBusinessVerification).
  showBusinessVerification: boolean
}

// Order here IS the tab order rendered on the page, and the fallback order
// used to pick a landing tab when the user opens the bare /organization-settings
// item (or a tab they don't have access to) — see organization-settings/index.tsx.
//
// RBAC (preserved from the old flat popover, see CLAUDE.md history):
//   - API keys: every authenticated user (unchanged — was true for all users)
//   - Users: canAccessUsers (not free plan AND user.view perm)
//   - Organization / IP Whitelisting / Sub-accounts / Voice / Messaging / Account limits: admin only
// Geo Permissions is no longer a tab here — it moved back out to its own
// standalone popover item + route (see SETTINGS_PATHS.geoPermissions below).
// Teams is no longer a tab here either — it moved to its own /teams page,
// reachable from the Human Specialist popover (SER-5859 follow-up).
export const getOrganizationSettingsTabs = ({
  isAdmin,
  canAccessUsers,
  ssoSelfServeEnabled,
  showBusinessVerification,
}: GetOrganizationSettingsTabsInput): OrganizationSettingsTab[] => [
  {
    key: 'general',
    title: 'Organization',
    path: ORGANIZATION_SETTINGS_TABS.general,
    visible: isAdmin,
  },
  {
    key: 'apiKeys',
    title: 'API keys',
    path: ORGANIZATION_SETTINGS_TABS.apiKeys,
    visible: true,
  },
  {
    // Enterprise SSO setup, admin only (SER-5921); hodor enforces admin too.
    key: 'sso',
    title: 'SAML SSO',
    path: ORGANIZATION_SETTINGS_TABS.sso,
    visible: isAdmin && ssoSelfServeEnabled,
    needsFlag: true,
  },
  {
    key: 'ipWhitelisting',
    title: 'IP allowlist',
    path: ORGANIZATION_SETTINGS_TABS.ipWhitelisting,
    visible: isAdmin,
  },
  {
    key: 'users',
    title: 'Users',
    path: ORGANIZATION_SETTINGS_TABS.users,
    visible: canAccessUsers,
  },
  {
    key: 'subAccounts',
    title: 'Sub-accounts',
    path: ORGANIZATION_SETTINGS_TABS.subAccounts,
    visible: isAdmin,
  },
  {
    key: 'voice',
    title: 'Voice settings',
    path: ORGANIZATION_SETTINGS_TABS.voice,
    visible: isAdmin,
  },
  {
    key: 'messaging',
    title: 'Messaging settings',
    path: ORGANIZATION_SETTINGS_TABS.messaging,
    visible: isAdmin,
  },
  {
    key: 'limits',
    title: 'Account limits',
    path: ORGANIZATION_SETTINGS_TABS.limits,
    visible: isAdmin,
  },
  {
    // Decided by the CAF read (UCC View), not by role, and hidden once verified.
    key: 'businessVerification',
    title: 'Business verification',
    path: ORGANIZATION_SETTINGS_TABS.businessVerification,
    visible: showBusinessVerification,
    needsFlag: true,
  },
]

// ===== Enterprise plan gates (mirrors the Verify/WhatsApp locks) =====
//
// Popover items and Organization-settings tabs that are Enterprise-only. Same
// shape as `LOCKED_TAB_SOURCES` in pages/logs/index.tsx: the entry maps the
// gated thing to the Buddy request source, and a locked click opens the upgrade
// flow instead of navigating. RBAC still decides *visibility* (a user without
// endpoint access doesn't see a locked Endpoints item at all) — the lock is a
// second, independent axis on top of it.
//
// The gate itself is `useUserPlan().isEnterpriseLocked` (plan loaded AND not
// Enterprise), so an Enterprise account never sees a lock and never flashes one
// while the profile loads. Upgrading to Enterprise removes the lock with no
// code change here.
const ENTERPRISE_LOCKED_ITEM_SOURCES: Partial<Record<string, EnterpriseRequestSource>> = {
  [SETTINGS_PATHS.endpoints]: 'endpoints',
}

const ENTERPRISE_LOCKED_TAB_SOURCES: Partial<
  Record<OrganizationSettingsTabKey, EnterpriseRequestSource>
> = {
  messaging: 'messaging',
}

/**
 * The Buddy upgrade source for a popover item that is locked for this user, or
 * `undefined` when the item is not plan-gated (or the user is on Enterprise).
 * A truthy result means "render ghosted + lock icon, and open the upgrade flow
 * on click instead of navigating".
 */
export const getLockedItemSource = (
  path: string,
  isEnterpriseLocked: boolean,
): EnterpriseRequestSource | undefined =>
  isEnterpriseLocked ? ENTERPRISE_LOCKED_ITEM_SOURCES[path] : undefined

/**
 * How a popover item should render while its entitlement is still resolving.
 *
 * The lock inputs read unlocked in flight, so a gated item would otherwise be
 * navigable during a cold query — the user lands on the page and only then meets
 * the CTA. `pending` holds the click without showing a lock, which would be the
 * worse guess for an entitled account.
 */
export const resolveItemGateState = (
  path: string,
  { isLocked, isLockLoaded }: { isLocked: boolean; isLockLoaded: boolean },
): { pending: boolean; lockedSource: EnterpriseRequestSource | undefined } => {
  const gated = ENTERPRISE_LOCKED_ITEM_SOURCES[path] !== undefined
  if (gated && !isLockLoaded) return { pending: true, lockedSource: undefined }
  return { pending: false, lockedSource: getLockedItemSource(path, isLocked) }
}

/** Same as getLockedItemSource, for an Organization settings tab. */
export const getLockedTabSource = (
  key: OrganizationSettingsTabKey,
  isEnterpriseLocked: boolean,
): EnterpriseRequestSource | undefined =>
  isEnterpriseLocked ? ENTERPRISE_LOCKED_TAB_SOURCES[key] : undefined

// Shallow popover items: User settings, Organization settings, Billing,
// Plans & Pricing, Endpoints (+ Logout, rendered separately by the popover).
// Each opens a page with its own top tabs instead of the old two-flat-section
// deep links.
export const getVisibleSettingsItems = ({
  isAdmin,
  canAccessUsers,
  canAccessBilling,
  canAccessEndpoints,
  ssoSelfServeEnabled,
  canViewTrustAndLegal,
}: GetVisibleSettingsItemsInput): GetVisibleSettingsItemsResult => {
  const organizationSettingsTabs = getOrganizationSettingsTabs({
    isAdmin,
    canAccessUsers,
    ssoSelfServeEnabled,
    // Cannot change the answer below: API keys is unconditional.
    showBusinessVerification: false,
  })
  const hasVisibleOrganizationTab = organizationSettingsTabs.some((tab) => tab.visible)

  return {
    profileSettings: {
      title: 'User settings',
      path: SETTINGS_PATHS.profileSettings,
      visible: true,
    },
    organizationSettings: {
      title: 'Organization settings',
      path: SETTINGS_PATHS.organizationSettings,
      // Shows if the user can see >=1 of its tabs — API keys is unconditional,
      // so in practice this is always true for any authenticated user; a
      // non-admin who opens it just lands on their permitted tab(s).
      visible: hasVisibleOrganizationTab,
    },
    billing: {
      title: 'Billing',
      path: SETTINGS_PATHS.billing,
      visible: canAccessBilling,
    },
    plansAndPricing: {
      title: 'Plans & Pricing',
      path: SETTINGS_PATHS.plansAndPricing,
      visible: canAccessBilling,
    },
    endpoints: {
      title: 'Endpoints',
      path: SETTINGS_PATHS.endpoints,
      visible: canAccessEndpoints,
    },
    // Standalone item again (was briefly a tab on Organization settings) —
    // admin-only, same gate as the rest of Organization settings' tabs.
    geoPermissions: {
      title: 'Geo Permissions',
      path: SETTINGS_PATHS.geoPermissions,
      visible: isAdmin,
    },
    trustAndLegal: {
      title: 'Trust & Legal Compliance',
      path: SETTINGS_PATHS.trustAndLegal,
      visible: canViewTrustAndLegal,
    },
  }
}
